import React, { useEffect, useContext, useRef, useMemo, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import Select from "react-select";
import slugify from "slugify";
import { gql, useQuery, useMutation } from "@apollo/client";
import StarekContext from "../../context/StarekContext";
import { longDateString } from "../../utils";
import ConfirmModal from "../../components/modals/ConfirmModal";
import {
    SidebarView,
    SidebarContext,
    SidebarContextProvider,
    useSyncedField,
} from "../../components/Sidebar";

const GET_PARTS_WITH_OPERATIONS = gql`
    query GetPartsWithOperations($where: PartWhereInput!) {
        parts(where: $where) {
            id
            name
            part_stages {
                id
                operation_id
                sequence_no
                standard_duration
                incremental_weight
                decremental_weight
                absolute_weight
                allowed_devices {
                    device_id
                    position
                }
                operation {
                    id
                    name
                    is_productive
                }
            }
        }
    }
`;

const GET_DEVICES_WITH_CAPABILITIES = gql`
    query GetDevicesWithCapabilities($where: ShadowDeviceWhereInput!) {
        shadowDevices(where: $where) {
            id
            name
            installed_at_zones {
                name
            }
            capabilities {
                operation_id
            }
        }
    }
`;

const GET_SHIFTS = gql`
    query GetShiftsForJobSchedule($where: ShiftWhereInput!) {
        shifts(where: $where) {
            id
            name
            start_time
            end_time
        }
    }
`;

const GET_SCHEDULED_ASSIGNMENTS = gql`
    query GetScheduledAssignments($where: JobScheduleWhereInput!) {
        jobSchedules(where: $where) {
            id
            name
            start_time
            end_time
            operations {
                id
                device_id
            }
        }
    }
`;

// Work hours are seconds from midnight, the same encoding the Shifts screen uses.
const formatShiftTime = (sec) => {
    if (sec == null) return "-";
    const d = new Date(0);
    d.setSeconds(sec);
    let h = d.getUTCHours();
    const m = d.getUTCMinutes().toString().padStart(2, "0");
    const ampm = h >= 12 ? "pm" : "am";
    h = h % 12 || 12;
    return `${h}:${m} ${ampm}`;
};

// Width of the name and description columns in MySQL.
const MAX_TEXT = 191;

const ADVANCE_OPERATIONS = gql`
    mutation AdvanceJobScheduleOperations($jobScheduleId: Int!) {
        advanceJobScheduleOperations(job_schedule_id: $jobScheduleId) {
            total_operations
            completed_operations
            completed_cycles
            cycle_completed_now
            stalled
            stalled_operation
            steps {
                id
                status
                status_source
            }
        }
    }
`;

const SET_OPERATION_STATUS = gql`
    mutation SetJobScheduleOperationStatus(
        $operationId: Int!
        $status: Int!
    ) {
        setJobScheduleOperationStatus(
            job_schedule_operation_id: $operationId
            status: $status
        ) {
            job_schedule_id
            total_operations
            completed_operations
            completed_cycles
            cycle_completed_now
        }
    }
`;

function JobScheduleSidebarContent({ plantId, onProgressChange, conflictsRef }) {
    const starekContext = useContext(StarekContext);
    const { entity } = useContext(SidebarContext);

    const [jobName, setJobName] = useSyncedField("name", "");
    const [jobDescription, setJobDescription] = useSyncedField(
        "description",
        "",
    );
    const [selectedPart, setSelectedPart] = useSyncedField("part", "");
    const [dateRange, setDateRange] = useSyncedField("dateRange", [
        new Date(),
        new Date(),
    ]);
    // The shift this run works, by id. The run follows the shift's hours, so editing
    // a Shift moves every run on it — that is the point of the link.
    const [shiftId, setShiftId] = useSyncedField("shiftId", null);
    // Planned good parts for the run — feeds actual-vs-target in Reporting
    // Phase 1 (CPRN00117-867). Empty string = no target.
    const [targetUnits, setTargetUnits] = useSyncedField("targetUnits", "");
    // One row per operation in the Part's sequence.
    const [operationRows, setOperationRows] = useSyncedField(
        "operationRows",
        [],
    );

    const previousDescriptionRef = useRef("");
    const previousSlugRef = useRef("");
    const userModifiedNameRef = useRef(false);
    const userModifiedDescriptionRef = useRef(false);
    const entityIdRef = useRef(null);
    // Rows whose devices the floor manager picked by hand are never re-recommended.
    const touchedRowsRef = useRef(new Set());

    const { data: partsData, loading: loadingParts } = useQuery(
        GET_PARTS_WITH_OPERATIONS,
        {
            client: starekContext.engineClient,
            skip: !plantId,
            // Sequences and device links change from other screens; never serve them from cache.
            fetchPolicy: "network-only",
            variables: {
                where: {
                    plant_id: { equals: plantId },
                    is_active: { equals: true },
                },
            },
        },
    );

    const { data: devicesData, loading: loadingDevices } = useQuery(
        GET_DEVICES_WITH_CAPABILITIES,
        {
            client: starekContext.engineClient,
            skip: !plantId,
            fetchPolicy: "network-only",
            variables: {
                where: {
                    plant_id: { equals: plantId },
                    is_active: { equals: true },
                },
            },
        },
    );

    const { data: shiftsData, loading: loadingShifts } = useQuery(GET_SHIFTS, {
        client: starekContext.engineClient,
        skip: !plantId,
        fetchPolicy: "network-only",
        variables: {
            where: {
                plant_id: { equals: plantId },
                is_active: { equals: true },
            },
        },
    });

    const { data: assignmentsData } = useQuery(GET_SCHEDULED_ASSIGNMENTS, {
        client: starekContext.engineClient,
        skip: !plantId,
        variables: { where: { plant_id: { equals: plantId } } },
        fetchPolicy: "network-only",
    });

    const [setOperationStatus, { loading: savingStatus }] = useMutation(
        SET_OPERATION_STATUS,
        { client: starekContext.engineClient },
    );

    const [advanceOperations] = useMutation(ADVANCE_OPERATIONS, {
        client: starekContext.engineClient,
    });
    const operationRowsRef = useRef(operationRows);
    useEffect(() => {
        operationRowsRef.current = operationRows;
    }, [operationRows]);
    const [telemetry, setTelemetry] = useState(null);

    /**
     * Ask the server to read telemetry and move the run on. Steps a person set are left
     * alone, so this can run whenever the panel opens without fighting the operator.
     */
    useEffect(() => {
        if (!entity?.id) return;
        let cancelled = false;
        advanceOperations({ variables: { jobScheduleId: Number(entity.id) } })
            .then(({ data }) => {
                if (cancelled) return;
                const result = data?.advanceJobScheduleOperations;
                if (!result) return;
                setTelemetry(result);
                // Rows were rendered from the entity before this ran; fold the new
                // statuses in so the badges match what the server just decided.
                if (result.steps?.length) {
                    const byId = new Map(
                        result.steps.map((step) => [
                            Number(step.id),
                            step.status,
                        ]),
                    );
                    setOperationRows(
                        (operationRowsRef.current || []).map((row) =>
                            row.id && byId.has(Number(row.id))
                                ? { ...row, status: byId.get(Number(row.id)) }
                                : row,
                        ),
                    );
                }
                if (result.cycle_completed_now) {
                    starekContext.showSuccess(
                        `Cycle complete — ${result.completed_cycles} recorded for this run`,
                    );
                }
                onProgressChange?.();
            })
            .catch(() => {
                // Telemetry is best-effort; the manual controls remain the fallback.
            });
        return () => {
            cancelled = true;
        };
    }, [entity?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const STATUSES = starekContext.types?.job_schedule?.operation_statuses || {
        PENDING: 0,
        RUNNING: 1,
        COMPLETED: 2,
    };

    const statusLabel = (status) => {
        if (status === STATUSES.COMPLETED) return "Completed";
        if (status === STATUSES.RUNNING) return "Running";
        return "Pending";
    };

    const statusVariant = (status) => {
        if (status === STATUSES.COMPLETED) return "success";
        if (status === STATUSES.RUNNING) return "primary";
        return "secondary";
    };

    /**
     * Drives one step of the run. The cycle roll-up happens server-side, so the reply
     * tells us both the new progress and whether this transition finished the cycle.
     */
    const handleStatusChange = async (row, index, nextStatus) => {
        if (!row.id) return;
        try {
            const { data } = await setOperationStatus({
                variables: { operationId: Number(row.id), status: nextStatus },
            });
            const progress = data?.setJobScheduleOperationStatus;

            setOperationRows(
                operationRows.map((r, i) =>
                    i === index ? { ...r, status: nextStatus } : r,
                ),
            );

            if (progress?.cycle_completed_now) {
                starekContext.showSuccess(
                    `Cycle complete — ${progress.completed_cycles} recorded for this run`,
                );
            }
            onProgressChange?.();
        } catch (err) {
            starekContext.showError(err);
        }
    };

    const completedCount = operationRows.filter(
        (r) => r.status === STATUSES.COMPLETED,
    ).length;
    const trackedRows = operationRows.filter((r) => r.id).length;

    const parts = useMemo(() => partsData?.parts || [], [partsData?.parts]);
    const devices = useMemo(
        () => devicesData?.shadowDevices || [],
        [devicesData?.shadowDevices],
    );
    const shifts = useMemo(() => shiftsData?.shifts || [], [shiftsData?.shifts]);

    const shiftOptions = useMemo(
        () =>
            shifts.map((s) => ({
                value: s.id,
                label: `${s.name} (${formatShiftTime(s.start_time)} - ${formatShiftTime(s.end_time)})`,
                start_time: s.start_time,
                end_time: s.end_time,
            })),
        [shifts],
    );

    const selectedShift = useMemo(
        () => shifts.find((s) => Number(s.id) === Number(shiftId)) || null,
        [shifts, shiftId],
    );
    const selectedShiftOption = useMemo(
        () => shiftOptions.find((o) => Number(o.value) === Number(shiftId)) || null,
        [shiftOptions, shiftId],
    );

    const selectedPartObj = useMemo(
        () => parts.find((p) => String(p.id) === String(selectedPart)) || null,
        [parts, selectedPart],
    );

    const partStages = useMemo(() => {
        if (!selectedPartObj) return [];
        return [...(selectedPartObj.part_stages || [])].sort(
            (a, b) => (a.sequence_no || 0) - (b.sequence_no || 0),
        );
    }, [selectedPartObj]);

    const deviceById = useMemo(() => {
        const map = {};
        devices.forEach((d) => {
            map[d.id] = d;
        });
        return map;
    }, [devices]);

    const deviceLabel = (deviceId) => {
        const device = deviceById[deviceId];
        if (!device) return deviceId;
        const zone = device.installed_at_zones?.[0]?.name;
        return `${device.id}${zone ? ` (${zone})` : ""}`;
    };

    /**
     * Devices already committed to another job schedule whose window overlaps the one
     * being edited. Used to keep the recommendation off machines that are spoken for.
     */
    const busyDeviceIds = useMemo(() => {
        const [start, end] = dateRange || [];
        if (!start || !end) return new Set();
        const startMs = new Date(start).getTime();
        const endMs = new Date(end).getTime();

        const busy = new Set();
        (assignmentsData?.jobSchedules || []).forEach((js) => {
            if (entity?.id && Number(js.id) === Number(entity.id)) return;
            const jsStart = new Date(js.start_time).getTime();
            const jsEnd = new Date(js.end_time).getTime();
            const overlaps = jsStart <= endMs && jsEnd >= startMs;
            if (!overlaps) return;
            (js.operations || []).forEach((op) => {
                if (op.device_id) busy.add(op.device_id);
            });
        });
        return busy;
    }, [assignmentsData?.jobSchedules, dateRange, entity?.id]);

    /**
     * Runs that overlap this window and use one of the same machines.
     *
     * The window is a booking range rather than continuous occupancy, so this is a
     * warning and never a block — two runs can share a fortnight and never collide in
     * practice. It exists so the clash is visible instead of silent.
     */
    const machineConflicts = useMemo(() => {
        const [start, end] = dateRange || [];
        if (!start || !end) return [];
        const startMs = new Date(start).getTime();
        const endMs = new Date(end).getTime();

        const mine = new Set(
            operationRows.map((row) => row.device_id).filter(Boolean),
        );
        if (mine.size === 0) return [];

        return (assignmentsData?.jobSchedules || [])
            .filter((js) => !(entity?.id && Number(js.id) === Number(entity.id)))
            .map((js) => {
                const jsStart = new Date(js.start_time).getTime();
                const jsEnd = new Date(js.end_time).getTime();
                if (jsStart > endMs || jsEnd < startMs) return null;

                const shared = [
                    ...new Set(
                        (js.operations || [])
                            .map((op) => op.device_id)
                            .filter((id) => id && mine.has(id)),
                    ),
                ];
                if (shared.length === 0) return null;

                return {
                    id: js.id,
                    name: js.name,
                    start_time: js.start_time,
                    end_time: js.end_time,
                    machines: shared,
                };
            })
            .filter(Boolean);
    }, [
        assignmentsData?.jobSchedules,
        dateRange,
        operationRows,
        entity?.id,
    ]);

    /**
     * Whether this save actually changes the booking — the window or the machines.
     *
     * Editing a name, a duration or driving step statuses leaves the booking alone, and
     * re-reporting an overlap the user did not just create is noise.
     */
    const bookingChanged = useMemo(() => {
        if (!entity?.id) return true;

        const [start, end] = dateRange || [];
        const storedStart = new Date(entity.start_time).getTime();
        const storedEnd = new Date(entity.end_time).getTime();
        if (
            !start ||
            !end ||
            new Date(start).getTime() !== storedStart ||
            new Date(end).getTime() !== storedEnd
        ) {
            return true;
        }

        const stored = [...(entity.operations || [])]
            .sort((a, b) => (a.sequence_no || 0) - (b.sequence_no || 0))
            .map((op) => op.device_id || "")
            .join("|");
        const current = operationRows.map((row) => row.device_id || "").join("|");
        return stored !== current;
    }, [entity, dateRange, operationRows]);

    // Handed to the wrapper through a ref so the save handler can read it without
    // pushing derived data back into form state.
    useEffect(() => {
        if (conflictsRef) {
            conflictsRef.current = bookingChanged ? machineConflicts : [];
        }
    }, [machineConflicts, bookingChanged, conflictsRef]);

    /**
     * Devices that may run an operation for this run: capable of it, and — when the Part
     * restricts the operation to specific machines — one of those.
     */
    const eligibleDevicesFor = (operationId, allowedDeviceIds) => {
        const capable = devices.filter((d) =>
            (d.capabilities || []).some(
                (c) => Number(c.operation_id) === Number(operationId),
            ),
        );
        if (!allowedDeviceIds || allowedDeviceIds.length === 0) return capable;
        const allowed = new Set(allowedDeviceIds);
        return capable.filter((d) => allowed.has(d.id));
    };

    const allowedDeviceIdsForRow = (row, index) => {
        const stage =
            partStages[index]?.operation_id === row.operation_id
                ? partStages[index]
                : partStages.find(
                      (s) => Number(s.operation_id) === Number(row.operation_id),
                  );
        return [...(stage?.allowed_devices || [])]
            .sort((a, b) => (a.position || 0) - (b.position || 0))
            .map((ad) => ad.device_id);
    };

    // Load an existing schedule, or reset for a new one.
    useEffect(() => {
        const currentEntityId = entity?.id || null;
        if (entityIdRef.current === currentEntityId && currentEntityId !== null) {
            return;
        }
        entityIdRef.current = currentEntityId;
        touchedRowsRef.current = new Set();

        if (entity) {
            setJobName(entity.name || "");
            setJobDescription(entity.description || "");
            setSelectedPart(entity.part_id || "");
            setDateRange([
                new Date(entity.start_time),
                new Date(entity.end_time),
            ]);
            setShiftId(entity.shift_id == null ? null : Number(entity.shift_id));
            setTargetUnits(entity.target_units == null ? "" : String(entity.target_units));

            const existingRows = [...(entity.operations || [])]
                .sort((a, b) => (a.sequence_no || 0) - (b.sequence_no || 0))
                .map((op) => ({
                    id: op.id,
                    status: op.status ?? 0,
                    operation_id: Number(op.operation_id),
                    operation_name: op.operation?.name || "",
                    sequence_no: op.sequence_no,
                    device_id: op.device_id || null,
                    planned_duration: op.planned_duration ?? "",
                    incremental_weight: op.incremental_weight ?? 0,
                    decremental_weight: op.decremental_weight ?? 0,
                    absolute_weight: op.absolute_weight ?? null,
                }));
            setOperationRows(existingRows);
            // Devices already saved are the manager's choice — never overwrite them.
            existingRows.forEach((_, index) =>
                touchedRowsRef.current.add(index),
            );
        } else {
            setJobName("");
            setJobDescription("");
            setSelectedPart("");
            setDateRange([new Date(), new Date()]);
            setShiftId(null);
            setTargetUnits("");
            setOperationRows([]);
        }

        userModifiedNameRef.current = false;
        userModifiedDescriptionRef.current = false;
        // Keyed on entity id alone: re-running would discard in-progress edits.
    }, [entity?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Rebuilds the operation rows from the Part's sequence and pre-fills each one with
     * the best available devices. Rows the manager has touched keep their selection.
     */
    useEffect(() => {
        if (!selectedPartObj) {
            if (!entity?.id) setOperationRows([]);
            return;
        }
        // Don't build rows until the device list has arrived, or every row would be
        // baked with an empty recommendation.
        if (loadingDevices) return;

        const taken = new Set();
        const rows = partStages.map((stage, index) => {
            // Ordered by preference: the first is the machine this step normally runs on.
            const allowedDeviceIds = [...(stage.allowed_devices || [])]
                .sort((a, b) => (a.position || 0) - (b.position || 0))
                .map((ad) => ad.device_id);

            const previous = operationRows[index];
            const keepExisting =
                touchedRowsRef.current.has(index) &&
                previous &&
                Number(previous.operation_id) === Number(stage.operation_id);

            if (keepExisting) {
                if (previous.device_id) taken.add(previous.device_id);
                return { ...previous, sequence_no: index + 1 };
            }

            // An operation is atomic: exactly one machine. The Part's default wins when
            // it is set and still eligible; otherwise recommend one that is free in this
            // window and not already used by an earlier step of this same run.
            const eligible = eligibleDevicesFor(
                stage.operation_id,
                allowedDeviceIds,
            );
            const preferred = allowedDeviceIds[0];
            const defaultDevice = eligible.find((d) => d.id === preferred);
            const free = eligible.filter(
                (d) => !busyDeviceIds.has(d.id) && !taken.has(d.id),
            );
            const fallback = eligible.filter((d) => !taken.has(d.id));
            const chosen =
                defaultDevice?.id ||
                (free[0] || fallback[0] || eligible[0])?.id ||
                null;
            if (chosen) taken.add(chosen);

            return {
                operation_id: Number(stage.operation_id),
                operation_name: stage.operation?.name || "",
                sequence_no: index + 1,
                device_id: chosen,
                // Seeded from the Part, overridable for this run.
                planned_duration: stage.standard_duration ?? "",
                // Score weights: snapshotted from the Part, not editable per run,
                // so a rebalanced route does not rescore a run already under way.
                incremental_weight: stage.incremental_weight ?? 0,
                decremental_weight: stage.decremental_weight ?? 0,
                absolute_weight: stage.absolute_weight ?? null,
            };
        });

        setOperationRows(rows);
        // Recommendation depends on the part, the window and who else is booked.
    }, [selectedPart, partStages, devices, busyDeviceIds, loadingDevices]); // eslint-disable-line react-hooks/exhaustive-deps

    const updateRow = (index, patch) => {
        touchedRowsRef.current.add(index);
        setOperationRows(
            operationRows.map((row, i) =>
                i === index ? { ...row, ...patch } : row,
            ),
        );
    };

    // Auto-generate description from the part and its sequence.
    useEffect(() => {
        if (entity?.id || userModifiedDescriptionRef.current) return;
        if (!selectedPartObj) return;

        const [start, end] = dateRange || [];
        let generated = `${selectedPartObj.name}`;
        if (operationRows.length) {
            generated += ` via ${operationRows
                .map((r) => r.operation_name)
                .filter(Boolean)
                .join(" → ")}`;
        }
        if (start && end) {
            generated += ` between ${longDateString(start)} and ${longDateString(end)}`;
        }
        if (selectedShift) {
            generated += ` on ${selectedShift.name}`;
        }

        // description is varchar(191): a long route would otherwise generate a value
        // the database rejects on save.
        const capped = generated.slice(0, MAX_TEXT);
        if (capped !== previousDescriptionRef.current) {
            previousDescriptionRef.current = capped;
            setJobDescription(capped);
        }
    }, [selectedPartObj, operationRows, dateRange, selectedShift, entity?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const hashDates = (start, end) => {
        if (!start || !end) return "";
        const str = `${new Date(start).toISOString()}-${new Date(end).toISOString()}`;
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = (hash << 5) - hash + str.charCodeAt(i);
            hash = hash & hash;
        }
        return Math.abs(hash).toString(36).substring(0, 8);
    };

    // Auto-generate a unique slug name.
    useEffect(() => {
        if (entity?.id || userModifiedNameRef.current) return;
        if (!selectedPartObj) return;

        const [start, end] = dateRange || [];
        const slugParts = [selectedPartObj.name];
        const firstDevice = operationRows[0]?.device_id;
        if (firstDevice) slugParts.push(firstDevice);
        const dateHash = hashDates(start, end);
        if (dateHash) slugParts.push(dateHash);

        const generatedSlug = slugify(slugParts.join(" "), {
            lower: true,
            strict: true,
            replacement: "-",
        });

        const cappedSlug = generatedSlug.slice(0, MAX_TEXT);
        if (cappedSlug !== previousSlugRef.current) {
            previousSlugRef.current = cappedSlug;
            setJobName(cappedSlug);
        }
    }, [selectedPartObj, operationRows, dateRange, entity?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleNameChange = (e) => {
        userModifiedNameRef.current = true;
        setJobName(e.target.value);
    };

    const handleDescriptionChange = (e) => {
        userModifiedDescriptionRef.current = true;
        setJobDescription(e.target.value);
    };

    return (
        <SidebarView entityName="Job Schedule" entityCode="job_schedule">
            <SidebarView.Page name="job_details" title="Job Details">
                <Form className="tw-flex tw-flex-col tw-gap-4">
                    <div>
                        <label className="tw-text-sm tw-font-medium tw-flex tw-items-center tw-gap-2">
                            Select Part{" "}
                            <span className="tw-text-red-500">*</span>
                            {loadingParts && (
                                <Spinner animation="border" size="sm" />
                            )}
                        </label>
                        <select
                            className="tw-w-full tw-border tw-rounded-md tw-p-2"
                            value={selectedPart}
                            onChange={(e) => {
                                touchedRowsRef.current = new Set();
                                setSelectedPart(e.target.value);
                            }}
                            disabled={loadingParts}
                        >
                            <option value="">Select Part</option>
                            {parts.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="tw-text-sm tw-font-medium">
                            Select Date & Time Range{" "}
                            <span className="tw-text-red-500">*</span>
                        </label>
                        <div className="tw-overflow-visible tw-relative">
                            <DateRangePicker
                                format="yyyy-MM-dd HH:mm"
                                value={dateRange}
                                onChange={(range) => {
                                    if (range) setDateRange(range);
                                }}
                                block
                                placement="auto"
                                container={null}
                                popupClassName="rsuite-datepicker-override"
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="tw-text-sm tw-font-medium tw-flex tw-items-center tw-gap-2">
                            Shift
                            {loadingShifts && (
                                <Spinner animation="border" size="sm" />
                            )}
                        </label>

                        <Select
                            options={shiftOptions}
                            value={selectedShiftOption}
                            placeholder={
                                shiftOptions.length
                                    ? "Select a shift (optional)"
                                    : "No shifts defined for this plant"
                            }
                            isLoading={loadingShifts}
                            isDisabled={shiftOptions.length === 0}
                            isClearable
                            onChange={(option) =>
                                setShiftId(option ? Number(option.value) : null)
                            }
                        />

                        <div className="tw-text-xs tw-text-gray-500 tw-mt-1">
                            {selectedShift ? (
                                <>
                                    Worked{" "}
                                    {formatShiftTime(selectedShift.start_time)} -{" "}
                                    {formatShiftTime(selectedShift.end_time)}
                                    {Number(selectedShift.end_time) <=
                                        Number(selectedShift.start_time) &&
                                        " (carries past midnight)"}
                                    . The run follows this shift, so changing its
                                    hours changes the run.
                                </>
                            ) : (
                                "Hours worked on each day of the range above. Leave empty to run the full range."
                            )}
                        </div>
                    </div>

                    {selectedPart && (
                        <div>
                            <label className="tw-text-sm tw-font-medium tw-flex tw-items-center tw-gap-2">
                                Route
                                {trackedRows > 0 && (
                                    <span className="tw-text-xs tw-font-normal tw-text-gray-500">
                                        — {completedCount} of {trackedRows}{" "}
                                        operations complete
                                    </span>
                                )}
                                {telemetry?.stalled && (
                                    <span
                                        className="badge bg-warning tw-text-xs"
                                        title="The machine for this step has been silent far longer than expected"
                                    >
                                        {telemetry.stalled_operation ||
                                            "A step"}{" "}
                                        stalled
                                    </span>
                                )}
                                {loadingDevices && (
                                    <Spinner animation="border" size="sm" />
                                )}
                            </label>

                            {trackedRows > 0 && (
                                <div className="tw-text-xs tw-text-gray-500 tw-mb-2">
                                    Steps advance from machine activity. Start,
                                    Complete and Reopen apply immediately and are
                                    never overwritten by telemetry.
                                </div>
                            )}

                            {operationRows.length === 0 ? (
                                <div className="tw-border tw-rounded-md tw-p-3 tw-text-sm tw-text-gray-500">
                                    This part has no route yet. Add one under
                                    Setup &rarr; Parts &rarr; Route.
                                </div>
                            ) : (
                                operationRows.map((row, index) => {
                                    const eligible = eligibleDevicesFor(
                                        row.operation_id,
                                        allowedDeviceIdsForRow(row, index),
                                    );
                                    const options = eligible.map((d) => ({
                                        value: d.id,
                                        label: `${deviceLabel(d.id)}${
                                            busyDeviceIds.has(d.id)
                                                ? " — busy in this window"
                                                : ""
                                        }`,
                                    }));
                                    const value = row.device_id
                                        ? {
                                              value: row.device_id,
                                              label: deviceLabel(row.device_id),
                                          }
                                        : null;

                                    const totalSeconds =
                                        Number(row.planned_duration) || 0;
                                    const minutes = totalSeconds
                                        ? Math.floor(totalSeconds / 60)
                                        : "";
                                    const seconds = totalSeconds
                                        ? totalSeconds % 60
                                        : "";
                                    const setDuration = (m, sec) => {
                                        const mm = parseInt(m || 0, 10);
                                        const ss = parseInt(sec || 0, 10);
                                        const total = mm * 60 + ss;
                                        updateRow(index, {
                                            planned_duration:
                                                Number.isNaN(total) ||
                                                total === 0
                                                    ? ""
                                                    : total,
                                        });
                                    };

                                    return (
                                        <div
                                            key={`${row.operation_id}-${index}`}
                                            className="tw-border tw-rounded-md tw-p-3 tw-mb-2"
                                        >
                                            <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                                                <div className="tw-flex tw-items-center tw-gap-2">
                                                    <span className="badge bg-secondary">
                                                        {index + 1}
                                                    </span>
                                                    <span className="tw-font-medium">
                                                        {row.operation_name}
                                                    </span>
                                                </div>
                                            </div>

                                            {loadingDevices ? (
                                                <div className="tw-text-sm tw-text-gray-500">
                                                    Loading machines...
                                                </div>
                                            ) : eligible.length === 0 ? (
                                                <div className="tw-text-sm tw-text-red-600">
                                                    No machine is available for
                                                    this step. Link one under
                                                    Devices &rarr; Capability,
                                                    or widen the part&apos;s
                                                    allowed machines.
                                                </div>
                                            ) : (
                                                <Select
                                                    options={options}
                                                    value={value}
                                                    placeholder="Select machine"
                                                    onChange={(val) =>
                                                        updateRow(index, {
                                                            device_id: val
                                                                ? val.value
                                                                : null,
                                                        })
                                                    }
                                                />
                                            )}

                                            <div className="tw-mt-2">
                                                <label className="tw-text-xs tw-text-gray-600">
                                                    Duration for this run
                                                </label>
                                                <div className="tw-flex tw-gap-2">
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        className="tw-w-full tw-border tw-rounded-md tw-p-2"
                                                        placeholder="Minutes"
                                                        value={minutes}
                                                        onChange={(e) =>
                                                            setDuration(
                                                                e.target.value,
                                                                seconds,
                                                            )
                                                        }
                                                    />
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        max={59}
                                                        className="tw-w-full tw-border tw-rounded-md tw-p-2"
                                                        placeholder="Seconds"
                                                        value={seconds}
                                                        onChange={(e) =>
                                                            setDuration(
                                                                minutes,
                                                                e.target.value,
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <div className="tw-text-xs tw-text-gray-500 tw-mt-1">
                                                    Prefilled from the part.
                                                    Change it to override for
                                                    this run only.
                                                </div>
                                            </div>

                                            {row.id && (
                                                <div className="tw-flex tw-items-center tw-justify-between tw-mt-3 tw-pt-2 tw-border-t">
                                                    <span
                                                        className={`badge bg-${statusVariant(row.status)}`}
                                                    >
                                                        {statusLabel(row.status)}
                                                    </span>
                                                    <div className="tw-flex tw-gap-2">
                                                        {row.status !==
                                                            STATUSES.RUNNING && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-primary"
                                                                disabled={
                                                                    savingStatus
                                                                }
                                                                onClick={() =>
                                                                    handleStatusChange(
                                                                        row,
                                                                        index,
                                                                        STATUSES.RUNNING,
                                                                    )
                                                                }
                                                            >
                                                                Start
                                                            </button>
                                                        )}
                                                        {row.status !==
                                                        STATUSES.COMPLETED ? (
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-success"
                                                                disabled={
                                                                    savingStatus
                                                                }
                                                                onClick={() =>
                                                                    handleStatusChange(
                                                                        row,
                                                                        index,
                                                                        STATUSES.COMPLETED,
                                                                    )
                                                                }
                                                            >
                                                                Complete
                                                            </button>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-secondary"
                                                                disabled={
                                                                    savingStatus
                                                                }
                                                                onClick={() =>
                                                                    handleStatusChange(
                                                                        row,
                                                                        index,
                                                                        STATUSES.PENDING,
                                                                    )
                                                                }
                                                            >
                                                                Reopen
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    )}

                    <div>
                        <label className="tw-text-sm tw-font-medium">
                            Name <span className="tw-text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            className="tw-w-full tw-border tw-rounded-md tw-p-2"
                            value={jobName}
                            onChange={handleNameChange}
                            maxLength={MAX_TEXT}
                            placeholder="Auto-generated unique name"
                        />
                    </div>

                    <div>
                        <label className="tw-text-sm tw-font-medium">
                            Description
                        </label>
                        <textarea
                            className="tw-w-full tw-border tw-rounded-md tw-p-2"
                            rows="4"
                            value={jobDescription}
                            onChange={handleDescriptionChange}
                            maxLength={MAX_TEXT}
                            placeholder="Auto-generated description"
                        />
                    </div>

                    <div>
                        <label className="tw-text-sm tw-font-medium">
                            Target Units (parts)
                        </label>
                        <input
                            type="number"
                            min="0"
                            className="tw-w-full tw-border tw-rounded-md tw-p-2"
                            value={targetUnits}
                            onChange={(e) => setTargetUnits(e.target.value)}
                            placeholder="Planned good parts for this run"
                        />
                        <p className="tw-text-xs tw-text-slate-500 tw-mt-1">
                            Used by the production reports for actual vs target.
                        </p>
                    </div>
                </Form>
            </SidebarView.Page>
        </SidebarView>
    );
}

const CANCELLED_BY_CONFLICT = "cancelled-at-conflict-warning";

/**
 * A saved run is where the Operations layer starts paying out: the steps advance off
 * machine telemetry, and the counts land in the operation report. Neither is obvious
 * from this panel, so the confirmation names both.
 */
const jobScheduleNextSteps = ({ data, entity, isNew, saved }) => {
    const name = data.name?.trim() || entity?.name || "Job Schedule";
    const jobId = saved?.id ?? entity?.id ?? null;

    const steps = [];

    if (jobId) {
        steps.push({
            label: "Track this run",
            why: "Steps advance from machine telemetry — you can also set one by hand.",
            path: ["Plan", "Job Schedules", name],
            to: `/job-schedules/${jobId}`,
        });
    }

    steps.push(
        {
            label: "See it on the calendar",
            why: "Check it sits where you expect against the other runs.",
            path: ["Plan", "Job Schedules", "Calendar"],
            to: "/job-schedules?view=calendar",
        },
        {
            label: "Watch the numbers come in",
            why: "Completed cycles and per-operation timings show up here once steps finish.",
            path: ["Track", "Quick View", "Operation"],
            to: "/reports?tab=operation",
        },
    );

    return {
        heading: `"${name}" ${isNew ? "scheduled" : "updated"}`,
        subheading: isNew
            ? "It runs on the machines named in each step, inside the shift you picked."
            : null,
        // No "Add another" here: this form's fields live above the tabs and so survive
        // the save, which would seed the next run with the last one's part and dates.
        allowAddAnother: false,
        steps,
    };
};

const JobScheduleSidebar = (props) => {
    const {
        show,
        onDiscard,
        item = null,
        plantId,
        onSave = async () => {},
        onDelete = async () => {},
        onProgressChange,
        onOpenSaved,
    } = props;
    const starekContext = useContext(StarekContext);
    const conflictsRef = useRef([]);
    const [conflictPrompt, setConflictPrompt] = useState({ open: false });

    // Resolves once the floor manager decides whether to schedule over the clash.
    const askAboutConflicts = (conflicts) =>
        new Promise((resolve) => {
            setConflictPrompt({ open: true, conflicts, resolve });
        });

    const answerConflicts = (proceed) => {
        conflictPrompt.resolve?.(proceed);
        setConflictPrompt({ open: false });
    };

    const handleSaveInternal = async (emittedData, entity) => {
        try {
            const jobName = emittedData.name?.trim();
            if (!jobName) {
                starekContext.showError("Job name is required");
                throw new Error("Job name is required");
            }
            if (!plantId) {
                starekContext.showError("Plant ID is required");
                throw new Error("Plant ID is required");
            }
            if (!emittedData.part) {
                starekContext.showError("Please select a Part");
                throw new Error("Please select a Part");
            }

            const operationRows = Array.isArray(emittedData.operationRows)
                ? emittedData.operationRows
                : [];

            if (operationRows.length === 0) {
                starekContext.showError(
                    "This part has no route. Define one before scheduling it.",
                );
                throw new Error("Part has no route");
            }

            const rowWithoutDevice = operationRows.find(
                (row) => !row.device_id,
            );
            if (rowWithoutDevice) {
                starekContext.showError(
                    `Select a machine for "${rowWithoutDevice.operation_name}"`,
                );
                throw new Error("Every step needs a machine");
            }

            const conflicts = conflictsRef.current || [];
            if (conflicts.length > 0) {
                const proceed = await askAboutConflicts(conflicts);
                if (!proceed) throw new Error(CANCELLED_BY_CONFLICT);
            }

            const [start, end] = emittedData.dateRange || [
                new Date(),
                new Date(),
            ];

            const payload = {
                id: entity?.id,
                name: jobName,
                description: emittedData.description || "",
                part_id: emittedData.part,
                // Kept in sync with the first assigned device so telemetry and reporting
                // joins that go through JobSchedule.device_id keep working.
                device_id: operationRows[0].device_id,
                start_time: start.toISOString(),
                end_time: end.toISOString(),
                shift_id:
                    emittedData.shiftId == null
                        ? null
                        : Number(emittedData.shiftId),
                target_units:
                    emittedData.targetUnits === "" ||
                    emittedData.targetUnits == null
                        ? null
                        : Number(emittedData.targetUnits),
                operations: operationRows.map((row, index) => ({
                    // Present for steps already in the database. Carrying it lets the
                    // save update those rows in place, so run progress survives an edit.
                    id: row.id ?? null,
                    operation_id: Number(row.operation_id),
                    sequence_no: index + 1,
                    device_id: row.device_id,
                    planned_duration:
                        row.planned_duration === "" ||
                        row.planned_duration === null ||
                        row.planned_duration === undefined
                            ? null
                            : Number(row.planned_duration),
                    incremental_weight: Number(row.incremental_weight) || 0,
                    decremental_weight: Number(row.decremental_weight) || 0,
                    absolute_weight:
                        row.absolute_weight === null ||
                        row.absolute_weight === undefined
                            ? null
                            : Number(row.absolute_weight),
                })),
            };

            const saved = await onSave(payload);

            starekContext.showSuccess(
                entity?.id
                    ? `Job Schedule "${jobName}" updated successfully`
                    : `Job Schedule "${jobName}" created successfully`,
            );

            return saved;
        } catch (error) {
            if (error?.message === CANCELLED_BY_CONFLICT) throw error;
            console.error("Error saving job schedule:", error);
            if (
                !error.message.includes("select") &&
                !error.message.includes("Select") &&
                !error.message.includes("required") &&
                !error.message.includes("sequence") &&
                !error.message.includes("machine")
            ) {
                starekContext.showError(
                    error?.message ||
                        "Failed to save job schedule. Please try again.",
                );
            }
            throw error;
        }
    };

    return (
        <>
        <SidebarContextProvider
            entity={item}
            show={show}
            onSave={handleSaveInternal}
            onDelete={onDelete}
            onDiscard={onDiscard}
            nextSteps={jobScheduleNextSteps}
            onOpenSaved={onOpenSaved}
        >
            <JobScheduleSidebarContent
                plantId={plantId}
                onProgressChange={onProgressChange}
                conflictsRef={conflictsRef}
            />
        </SidebarContextProvider>

        <ConfirmModal
            open={conflictPrompt.open}
            type="warning"
            title="Machines are already booked in this window"
            confirmText="Schedule anyway"
            cancelText="Go back"
            onConfirm={() => answerConflicts(true)}
            onClose={() => answerConflicts(false)}
        >
            <div className="text-dark">
                <p>
                    These runs overlap the dates you picked and use the same
                    machines:
                </p>
                <ul className="mb-2 ps-3">
                    {(conflictPrompt.conflicts || []).map((clash) => (
                        <li key={clash.id} className="mb-1">
                            <strong>{clash.name}</strong>
                            <div className="small text-muted">
                                {clash.machines.join(", ")} ·{" "}
                                {longDateString(new Date(clash.start_time))} to{" "}
                                {longDateString(new Date(clash.end_time))}
                            </div>
                        </li>
                    ))}
                </ul>
                <p className="small text-muted mb-0">
                    A schedule reserves a date range, not the machine itself, so
                    this may be fine — two runs can share a window and never
                    collide. Check the work hours before continuing.
                </p>
            </div>
        </ConfirmModal>
        </>
    );
};

export default JobScheduleSidebar;
